import { DeviceStatusInfo } from "@fairfleet/geotab/types";
import { useEffect, useMemo, useState } from "react";
import { useStatusInfosByIds } from "./useStatusInfosByIds";

export function useDeviceActivityStatus(
  devices: { id: string }[] | undefined,
  isOpen: boolean,
): { statusInfoMap: Record<string, DeviceStatusInfo>; isError: boolean } {
  // Taken from the query data rather than a derived array, which is fresh on every render, and
  // sorted, because this array is part of the status query key: in search order it would change
  // whenever two vehicles swapped places and refetch for nothing.
  //
  // Empty while the list is closed, which switches the query off. The status is only ever seen
  // inside the open list, and asking for it regardless would poll a fleet-sized set of vehicles
  // every fifteen seconds — in the background too — for as long as the page stays open.
  const deviceIds = useMemo(
    () => (isOpen ? (devices ?? []).map(device => device.id).sort() : []),
    [devices, isOpen],
  );
  const { data, isError, isPlaceholderData, isFetching, dataUpdatedAt, refetch } =
    useStatusInfosByIds(deviceIds);

  // When the list was opened. A vehicle's status changes from one second to the next, so only an
  // answer asked for during this opening says what it is doing now.
  const [openedAt, setOpenedAt] = useState<number | null>(null);
  useEffect(() => {
    setOpenedAt(isOpen ? Date.now() : null);
  }, [isOpen]);

  const isFromEarlier =
    openedAt === null || dataUpdatedAt === undefined || dataUpdatedAt < openedAt;

  // Anything older — placeholder data of a previous search, or the cache of an earlier opening —
  // does not count as known: the icon shows "unknown" until this opening's answer arrives.
  const isCarriedOver = !isOpen || isPlaceholderData || isFromEarlier;
  const statusInfoMap = isCarriedOver || !data ? {} : data;

  // The app's ten-minute stale time means a reopened list would find its old answer in the cache
  // and not ask again until the next poll, up to fifteen seconds later. So every opening asks at
  // once — unless the query is already fetching, as it does for a list it has never seen.
  const [refreshedFor, setRefreshedFor] = useState<number | null>(null);
  useEffect(() => {
    if (
      openedAt === null ||
      refreshedFor === openedAt ||
      deviceIds.length === 0 ||
      !data ||
      isPlaceholderData ||
      isFetching ||
      !isFromEarlier
    ) {
      return;
    }
    setRefreshedFor(openedAt);
    void refetch?.();
  }, [
    openedAt,
    refreshedFor,
    deviceIds,
    data,
    isPlaceholderData,
    isFetching,
    isFromEarlier,
    refetch,
  ]);

  return { statusInfoMap, isError };
}
